import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useState } from "react";
import {
  type TopOccupationWithSkills,
} from "~/lib/skills-seo-queries";
import { generateHreflangLinks } from "~/lib/seo-utils";

const fetchOccupationsWithSkills = createServerFn({ method: "GET" }).handler(
  async (): Promise<TopOccupationWithSkills[]> => {
    const { getOccupationsWithSkills } = await import("~/lib/skills-seo-queries");
    return getOccupationsWithSkills(200);
  },
);

export const Route = createFileRoute("/skills/")({
  loader: () => fetchOccupationsWithSkills(),
  head: () => ({
    meta: [
      { title: "Skills nach Beruf — ESCO Kompetenzübersicht | Alchema" },
      {
        name: "description",
        content:
          "Entdecke welche Skills für deinen Beruf essentiell sind. Basierend auf dem europäischen ESCO-Framework — über 13.000 Kompetenzen für 3.000+ Berufe.",
      },
      { property: "og:title", content: "Skills nach Beruf — ESCO Kompetenzübersicht | Alchema" },
      { property: "og:description", content: "Entdecke welche Skills für deinen Beruf essentiell sind. Basierend auf dem europäischen ESCO-Framework." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://alchema.eu/skills" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Skills nach Beruf | Alchema" },
      { name: "twitter:description", content: "Welche Kompetenzen braucht dein Beruf? ESCO-Framework Kompetenzübersicht." },
    ],
    links: generateHreflangLinks("/skills"),
  }),
  component: SkillsIndexPage,
});

const ISCO_LABELS: Record<string, string> = {
  "0": "Militär",
  "1": "Führungskräfte",
  "2": "Akademische Berufe",
  "3": "Techniker & gleichrangige Berufe",
  "4": "Bürokräfte",
  "5": "Dienstleistungsberufe",
  "6": "Land- & Forstwirtschaft",
  "7": "Handwerksberufe",
  "8": "Anlagen- & Maschinenbediener",
  "9": "Hilfsarbeitskräfte",
};

function SkillsIndexPage() {
  const occupations = Route.useLoaderData();
  const [search, setSearch] = useState("");

  const filtered = search
    ? occupations.filter((o: TopOccupationWithSkills) =>
        o.labelDe.toLowerCase().includes(search.toLowerCase()),
      )
    : occupations;

  // Group by ISCO major group (first digit)
  const grouped = new Map<string, TopOccupationWithSkills[]>();
  for (const occ of filtered) {
    const group = occ.iscoCode?.charAt(0) ?? "?";
    if (!grouped.has(group)) grouped.set(group, []);
    grouped.get(group)!.push(occ);
  }

  return (
    <div style={{ backgroundColor: "var(--bg)" }}>
      <section className="mx-auto max-w-4xl px-6 pb-24 pt-20">
        <h1
          className="text-3xl font-bold tracking-tight"
          style={{ color: "var(--text)" }}
        >
          Skills nach Beruf
        </h1>
        <p
          className="mt-3 max-w-xl text-base leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          Welche Kompetenzen braucht dein Beruf? Basierend auf dem europäischen
          ESCO-Framework (European Skills, Competences, Qualifications and
          Occupations).
        </p>

        <div className="mt-8">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Beruf suchen..."
            className="w-full max-w-md rounded-[10px] border px-4 py-2.5 text-sm outline-none"
            style={{
              borderColor: "var(--border)",
              background: "var(--surface)",
              color: "var(--text)",
            }}
          />
        </div>

        {occupations.length === 0 ? (
          <p className="mt-12 text-sm" style={{ color: "var(--text-muted)" }}>
            Keine Berufe mit Skills-Daten verfügbar.
          </p>
        ) : (
          <div className="mt-8 space-y-10">
            {[...grouped.entries()].map(([group, occs]) => (
              <div key={group}>
                <h2
                  className="text-lg font-semibold"
                  style={{ color: "var(--text)" }}
                >
                  {ISCO_LABELS[group] ?? `Gruppe ${group}`}
                </h2>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {occs.map((occ) => (
                    <Link
                      key={occ.slug}
                      to="/skills/$occupation"
                      params={{ occupation: occ.slug }}
                      className="flex items-center justify-between rounded-xl border px-4 py-3 transition hover:shadow-sm"
                      style={{
                        borderColor: "var(--border)",
                        background: "var(--surface)",
                      }}
                    >
                      <span
                        className="text-sm font-medium"
                        style={{ color: "var(--text)" }}
                      >
                        {occ.labelDe}
                      </span>
                      <span
                        className="text-xs"
                        style={{ color: "var(--text-muted)" }}
                      >
                        {occ.skillCount} Skills
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="mt-16 text-xs" style={{ color: "var(--text-muted)" }}>
          Quelle: ESCO (European Skills, Competences, Qualifications and
          Occupations) — Europäische Kommission.
        </p>
      </section>
    </div>
  );
}
